import "./ResultsWidget.css";
import React from "react";
import * as utils from "common/utils";
import SummaryRow from "../PlanningReport/SummaryRow";
import Watermark from "components/Watermark";

const ExcludedRemovedTypes = ["AtmosphericRemoved", "Water"];

const NutrientBudgetWidget = ({ refData, before, after, nutrient, summaryOnly }) => {
    const afterNB = after && after.details.currentResults && after.details.currentResults.nutrientBudget && after.details.currentResults.nutrientBudget.nutrientCategories;
    if (!afterNB) return <></>;

    const beforeNB = before && before.details.currentResults && before.details.currentResults.nutrientBudget && before.details.currentResults.nutrientBudget.nutrientCategories;

    const additions = [];
    const biological = [];
    const products = [];

    const getCategoryDataForName = (categoryType) => {
        const after = afterNB[categoryType] && afterNB[categoryType].total && afterNB[categoryType].total.nutrients && afterNB[categoryType].total.nutrients[nutrient.value] ? afterNB[categoryType] : undefined;
        const before = beforeNB && beforeNB[categoryType] && beforeNB[categoryType].total && beforeNB[categoryType].total.nutrients && beforeNB[categoryType].total.nutrients[nutrient.value] ? beforeNB[categoryType] : undefined;

        if (after || before) {
            const item = utils.getRefDataItem(refData.nutrientBudgetCategoryTypes, categoryType);
            const effect = (after && after.effect) || (before && before.effect);

            return { name: item ? item.text : "", type: categoryType, effect: effect, description: item && item.ext ? item.ext : "", loss: after ? after.total.nutrients[nutrient.value] : 0, before: before ? before.total.nutrients[nutrient.value] : 0 };
        }
        return undefined;
    };

    const addCategoryToArray = (categoryType) => {
        const data = getCategoryDataForName(categoryType);

        if (data) {
            if (data.effect === "Added") additions.push(data);
            else if (data.effect === "Removed") {
                if (!ExcludedRemovedTypes.includes(categoryType)) products.push(data);
            } else biological.push(data);
        }
    };

    const getSubCategoryDataForName = (categoryName, subCategoryName) => {
        const sc = afterNB[categoryName] && afterNB[categoryName].subTotals[subCategoryName].nutrients && afterNB[categoryName].subTotals[subCategoryName].nutrients[nutrient.value];
        const scBefore = beforeNB && beforeNB[categoryName] && beforeNB[categoryName].subTotals[subCategoryName].nutrients && beforeNB[categoryName].subTotals[subCategoryName].nutrients[nutrient.value];

        if (sc || scBefore) {
            const item = utils.getRefDataItem(refData.nutrientResultsTypes, subCategoryName);
            return { name: item ? item.text : "", type: subCategoryName, description: item && item.ext ? item.ext : "", loss: sc ? sc : 0, before: scBefore ? scBefore : 0 };
        }
        return undefined;
    };

    refData.nutrientBudgetCategoryTypes.forEach((c) => addCategoryToArray(c.value));

    const losses = Object.keys(afterNB.Water.subTotals)
        .map((k) => getSubCategoryDataForName("Water", k))
        .filter((a) => a);
    const atmosLosses = Object.keys(afterNB.AtmosphericRemoved.subTotals)
        .map((k) => getSubCategoryDataForName("AtmosphericRemoved", k))
        .filter((a) => a);

    const totalAdditions = additions.reduce((t, a) => (t += a.loss), 0);
    const totalAdditionsBefore = beforeNB && additions.filter((a) => a.before).reduce((t, a) => (t += a.before), 0);
    const totalProducts = products.reduce((t, a) => (t += a.loss), 0);
    const totalProductsBefore = beforeNB && products.filter((a) => a.before).reduce((t, a) => (t += a.before), 0);
    const totalLosses = losses.reduce((t, a) => (t += a.loss), 0);
    const totalLossesBefore = beforeNB && losses.filter((a) => a.before).reduce((t, a) => (t += a.before), 0);
    const totalAtmosLosses = atmosLosses.reduce((t, a) => (t += a.loss), 0);
    const totalAtmosLossesBefore = beforeNB && atmosLosses.filter((a) => a.before).reduce((t, a) => (t += a.before), 0);
    const totalBiological = biological.reduce((t, a) => (t += a.loss), 0);
    const totalBiologicalBefore = beforeNB && biological.filter((a) => a.before).reduce((t, a) => (t += a.before), 0);

    const TotalColourPanel = ({ total, beforeTotal, name }) => {
        const diff = total - beforeTotal || 0;
        const increased = diff > 0;
        const decreased = diff < 0;
        const diffPercentage = total !== 0 && total !== "0" ? (100 * diff) / parseFloat(total) : 100;
        const diffPerFormatted = beforeTotal === 0 && total > 0 ? "+100%" : diffPercentage > 0 ? `+${utils.round(diffPercentage, 1)}%` : `${utils.round(diffPercentage, 1)}%`;
        const change = increased ? `+${diff.toLocaleString()}` : decreased ? diff.toLocaleString() : "-";

        const changeTextClassName = decreased ? "u-textError u-textBold" : "u-textSuccess u-textBold";
        const changeArrowClassName = increased ? "icon-arrow-up1 IconLink--compressed" : decreased ? "icon-arrow-down1 IconLink--compressed" : "";

        return (
            <div className={`NutrientPanelSmall ColourPanel--${nutrient.value.toLowerCase()}loss`}>
                <div className="u-pl-md">
                    <h5>{name}</h5>
                    <div>
                        <div className="HeroValue-item-number">
                            {total.toLocaleString()} <small>kg/ha</small>
                        </div>
                        {diff !== 0 && (
                            <div className="u-flex u-flexJustifyEnd u-flexAlignItemsCenter">
                                <div className="u-text-sm">({diffPerFormatted})</div>
                                <div className="u-flex u-flexJustifyBetween u-flexAlignItemsCenter u-textBold u-textSecondary u-ml-xs">
                                    <span>{change.toLocaleString()}</span>
                                    <span className={`${changeArrowClassName} ${changeTextClassName}`}></span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const NutrientTable = ({ categories, title, description }) => {
        const total = categories.reduce((t, a) => (t += a.loss), 0);
        const beforeTotal = before && categories.filter((a) => a.before).reduce((t, a) => (t += a.before), 0);

        return (
            categories &&
            categories.length > 0 && (
                <div className="ReportSection u-page-break-avoid">
                    <h2>{title}</h2>
                    <p className="u-pb-md">{description}</p>
                    <div className="Table Table-Compressed">
                        <Watermark />
                        <table>
                            <thead>
                                <tr>
                                    <th data-width="25"></th>
                                    <th></th>

                                    {before && (
                                        <th className="u-textCenter u-before" data-width="12.5">
                                            {utils.truncateText(before.displayName, 35)}
                                        </th>
                                    )}
                                    <th className={before ? "u-after u-textCenter" : "u-textCenter"} data-width="12.5">
                                        {utils.truncateText(after.displayName, 35)}
                                    </th>
                                    {before && (
                                        <th className="u-textCenter" data-width="16.7">
                                            Change
                                        </th>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {categories.map((c, i) => {
                                    return <SummaryRow key={"category-row-" + c.name} category={c.name} rowClass={`Table-tr--${nutrient.value.toLowerCase()}loss Table-tr--start`} baseValue={before && (c.before || 0)} actualValue={c.loss} label={c.description} />;
                                })}
                                <SummaryRow category="Total" rowClass={`Table-tr--${nutrient.value.toLowerCase()}loss Table-tr--middle`} baseValue={beforeTotal} actualValue={total} label=" " />
                            </tbody>
                        </table>
                    </div>
                </div>
            )
        );
    };

    return (
        <div>
            <h1>{nutrient.text}</h1>
            <p className="u-pb-md">{`Nutrients are brought onto the farm and taken up by plants that are eaten by animals. Animals move around the farm and deposit nutrients in the form of urine and dung. Nutrients are removed in the form of products (meat, crops and milk). The difference between the nutrients added and products removed is the ${nutrient.value} surplus. Remaining nutrients undergo various biological processes, are lost to the atmosphere and when drainage occurs may leach or runoff from the farm.`}</p>
            <div className="u-flex u-mt-0 u-flex-wrap">
                <TotalColourPanel total={totalAdditions} beforeTotal={totalAdditionsBefore} name="Additions" />
                <div className="Arithmetic-symbols">-</div>
                <TotalColourPanel total={totalProducts} beforeTotal={totalProductsBefore} name="Products" />
                <div className="Arithmetic-symbols">=</div>
                <TotalColourPanel total={totalBiological} beforeTotal={totalBiologicalBefore} name="Transfers" />
                {(totalAtmosLosses !== 0 || totalAtmosLossesBefore !== 0) && (
                    <>
                        <div className="Arithmetic-symbols">+</div>
                        <TotalColourPanel total={totalAtmosLosses} beforeTotal={totalAtmosLossesBefore} name="Loss to air" />
                    </>
                )}
                <div className="Arithmetic-symbols">+</div>
                <TotalColourPanel total={totalLosses} beforeTotal={totalLossesBefore} name="Other losses" />
            </div>
            <div className="u-ml-sm">
                <sub style={{ lineHeight: "1rem" }}>{`1 - ${nutrient.text} surplus is total additions minus product removed (${totalAdditions - totalProducts} kg/ha)`}</sub>
            </div>
            <div className="u-ml-sm">
                <sub style={{ lineHeight: "1rem" }}>{`2 - The numbers in the nutrient budget have been rounded and so may not balance exactly`}</sub>
            </div>

            {!summaryOnly && (
                <>
                    <NutrientTable categories={additions} title={`${nutrient.text} brought onto farm`} description="Nutrients added to the farm via supplements, climate, fertiliser and effluent." />
                    <NutrientTable categories={products} title={`${nutrient.text} removed as product`} description="Nutrients removed from the farm as product and as supplements. The difference between this and nutrients added is then susceptible to leaching or runoff from the farm." />
                    <NutrientTable categories={biological} title={`Transfer of nutrients`} description="The biological processes that change nutrients available on farm. These nutrients are not taken up by plants and so are removed from the nutrient pool. Also includes the balance of the nutrients in supplements that are transferred to/from storage. " />
                    {(totalAtmosLosses !== 0 || totalAtmosLossesBefore !== 0) && <NutrientTable categories={atmosLosses} title={`${nutrient.text} lost to the atmosphere`} description="The nutrients lost into the atmosphere through volatilisation and denitrification." />}
                    <NutrientTable categories={losses} title={`Other ${nutrient.text} lost from the farm`} description="The nutrients lost from runoff, leaching or directly into water. This is where the excess nutrients runoff or drain from the farm due to water movement (drainage), or are deposited directly into water ways." />
                </>
            )}
        </div>
    );
};

export default NutrientBudgetWidget;
